"use client";

import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { DesignTheme, designThemes, themeStyleCache } from "@/lib/design-themes";

interface DesignThemeContextType {
  designTheme: DesignTheme;
  setDesignTheme: (theme: DesignTheme) => void;
}

const DesignThemeContext = createContext<DesignThemeContextType | undefined>(undefined);

// Script to inject into head to prevent flash
// Uses pre-calculated CSS cache for optimal performance
const themeScript = `
(function() {
  const stored = localStorage.getItem('design-theme') || 'green';
  const cache = ${JSON.stringify(themeStyleCache)};

  if (cache[stored]) {
    const lightCss = cache[stored].light;
    const darkCss = cache[stored].dark;

    // Create style element with both light and dark modes
    const style = document.createElement('style');
    style.id = 'design-theme-vars';
    style.textContent = ':root{' + lightCss + '} .dark{' + darkCss + '}';
    document.head.appendChild(style);
  }
})();
`;

export function DesignThemeScript() {
  // Generate inline style string for SSR
  const defaultTheme = designThemes.green;
  const lightVars = defaultTheme.cssVars.light;
  const darkVars = defaultTheme.cssVars.dark;
  
  const styleContent = `
    :root {
      ${Object.entries(lightVars).map(([key, value]) => `${key}: ${value};`).join('\n      ')}
      ${defaultTheme.gradient ? `--background-gradient: ${defaultTheme.gradient.light};` : ''}
    }
    .dark {
      ${Object.entries(darkVars).map(([key, value]) => `${key}: ${value};`).join('\n      ')}
      ${defaultTheme.gradient ? `--background-gradient: ${defaultTheme.gradient.dark};` : ''}
    }
  `;
  
  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: styleContent }} />
      <script
        dangerouslySetInnerHTML={{ __html: themeScript }}
        suppressHydrationWarning
      />
    </>
  );
}

export function DesignThemeProvider({ children }: { children: ReactNode }) {
  const [mounted, setMounted] = useState(false);
  const [designTheme, setDesignThemeState] = useState<DesignTheme>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('design-theme') as DesignTheme;
      if (stored && designThemes[stored]) {
        return stored;
      }
    }
    return 'green';
  });

  /* eslint-disable @eslint-react/set-state-in-effect */
  useEffect(() => {
    setMounted(true);
  }, []);
  /* eslint-enable @eslint-react/set-state-in-effect */

  useEffect(() => {
    if (!mounted) return;

    // Use pre-calculated CSS text with a dedicated style element
    // This reduces recalculations from ~20 setProperty calls to 1 textContent assignment
    const lightCss = themeStyleCache[designTheme].light;
    const darkCss = themeStyleCache[designTheme].dark;

    // Get or create a dedicated style element for theme variables
    // SSR script may have already created this element
    let styleElement = document.getElementById('design-theme-vars') as HTMLStyleElement;
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = 'design-theme-vars';
      document.head.appendChild(styleElement);
    }

    // Single DOM operation to update all theme variables
    // Include both light and dark modes with proper selectors for next-themes
    styleElement.textContent = `:root{${lightCss}} .dark{${darkCss}}`;
  }, [designTheme, mounted]);

  const setDesignTheme = (theme: DesignTheme) => {
    setDesignThemeState(theme);
    localStorage.setItem('design-theme', theme);
  };

  return (
    <DesignThemeContext.Provider value={{ designTheme, setDesignTheme }}>
      {children}
    </DesignThemeContext.Provider>
  );
}

export function useDesignTheme() {
  const context = useContext(DesignThemeContext);
  if (!context) {
    throw new Error('useDesignTheme must be used within DesignThemeProvider');
  }
  return context;
}
